<script lang="ts" setup>
	import type { TableRowSelection } from 'ant-design-vue/lib/table/interface'
	import { useAPI } from '@/composables/useApi'
	import type { ClassContext, IRequestResponse, PaginationContext, RecordSchema } from '~/types'

	const props = withDefaults(
		defineProps<{
			rowSelection?: TableRowSelection
			source?: string
			records?: PaginationContext | null
			columns: TableColumn[]
			loading?: boolean
			pagination?: boolean
			cache?: number
			networkKey?: string
			filter?: RecordSchema
			emptyTextPlaceholder?: string
			paginationWrapperClass?: ClassContext
		}>(),
		{
			pagination: true
		}
	)
	const tableRecord = ref<RecordSchema[]>([])
	const paginationList = ref<PaginationContext>({
		current_page: 0,
		per_page: 0,
		total_pages: 0,
		path: '',
		items: [],
		search: '',
		limit: 10
	})

	defineEmits<{
		(event: 'nextPage', param: number): void
		(event: 'update:record', param: PaginationContext): void
		(event: 'update:loading', param: boolean): void
	}>()
	const route = useRoute()
	const { replace } = useRouter()
	const loadingData = shallowRef(false)
	const slots = useSlots()
	const currentPage = computed(() => {
		return parseInt(route.query?.currentPage as string, 0) || 1
	})

	const hasFilter = computed(() => {
		return JSON.stringify(props?.filter || {})
	})
	const setPagination = (arg: PaginationContext) => {
		paginationList.value = {
			...arg,
			current_page: currentPage.value
		}
		const items = Array.isArray(arg.items) ? arg.items : []
		tableRecord.value = items.map((item: unknown, index: number) => ({
			...(item as Record<string, unknown>),
			key: index
		}))
	}
	const makeRequest = async (path: string, page: number) => {
		return await resolve(async () => {
			// Set pagination url for navigation
			const { data, error } = await useAPI<IRequestResponse<PaginationContext>>(
				`${props.source || path}?${createQueryParam({
					page,
					...props.filter
				})}`,
				{
					keepalive: true,
					key: props.networkKey
				}
			)
			if (data.value) {
				setPagination(data.value?.data || [])
			}
			return { error, data }
		}, loadingData)
	}
	const switchPages = async (page = currentPage.value || 0) => {
		const path: string | null = paginationList.value?.path?.replace('/api/v2', '') || null
		if (path) {
			const result = await makeRequest(path, page)
			if (!result?.error.value) {
				replace({
					query: {
						...route.query,
						currentPage: page
					}
				})
			}
		}
	}
	const setTableRecords = () => {
		if (props?.records?.items) {
			tableRecord.value = props.records?.items as RecordSchema[]
			setPagination(props.records)
		}
	}

	onMounted(() => {
		if (props.source) {
			makeRequest(props.source, currentPage.value)
		} else {
			setTableRecords()
		}
	})

	watch(
		() => props?.records?.items,
		() => {
			setTableRecords()
		}
	)

	watch(
		() => hasFilter.value,
		e => {
			if (e) {
				switchPages()
			}
		},
		{
			immediate: false,
			flush: 'post'
		}
	)
</script>

<template>
	<div>
		<shared-table-loader :loading="loading || loadingData" :columns="columns">
			<slot
				v-if="currentPage < 1 && !tableRecord.length && slots['empty-cell'] && !filter?.search"
				name="empty-cell"
			>
				<shared-empty-state title="No Records" description="You don't have any records at the moment.">
					<template #icon>
						<lazy-tools-icon :height="120" :width="120" dir="fill" icon="invoice" />
					</template>
				</shared-empty-state>
			</slot>
			<a-table
				v-else
				:columns="columns"
				:data-source="tableRecord"
				:row-selection="rowSelection"
				:pagination="false"
			>
				<template #bodyCell="content">
					<span v-if="content?.text === 'sn'">{{ content?.index + 1 }}</span>
					<slot
						name="bodyCell"
						v-bind="{
							...content,
							index:
								currentPage > 1
									? (Number(paginationList.per_page) * Number(paginationList.current_page)) / 2 +
										(content.index + 1)
									: content.index + 1
						}"
					/>
				</template>
				<template #emptyText>
					<div class="py-16">
						<slot name="empty">
							<shared-empty-state
								title-class="text-gray-400"
								:title="
									emptyTextPlaceholder ||
									(filter?.search ? `${String(filter.search)} is not found` : '') ||
									'Empty Record'
								"
								class="space-y-4 py-12"
								icon-position="top"
							>
								<template #icon>
									<lazy-tools-icon
										dir="fill"
										icon="mcg-cap-lg-gray"
										class="cursor-pointer"
										svg-class="md:h-[120px] md:w-[120px] h-[80px] w-[80px]"
										path-class="fill-gray-300"
									/>
								</template>
							</shared-empty-state>
						</slot>
					</div>
				</template>
			</a-table>
		</shared-table-loader>
		<div v-if="pagination" class="item-center flex justify-end" :class="paginationWrapperClass || 'mt-2'">
			<shared-pagination
				:current-page="currentPage"
				:total-items="Number(paginationList.total_pages)"
				:per-page="Number(paginationList.per_page)"
				@page-changed="switchPages"
			/>
		</div>
	</div>
</template>
